import {ChangeDetectionStrategy, Component, computed, inject, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {OverlayRef} from '@angular/cdk/overlay';
import {EMPTY, Subject} from 'rxjs';
import {catchError, filter, switchMap, takeUntil} from 'rxjs/operators';
import {PANEL_DATA} from '../../../../commons/angular/services/panel.service';
import {AccountsHttpService} from '../../../../account/angular/services/accounts-http.service';
import {AccountModel} from '../../../../account/angular/models/account.model';

export interface TimecardAccountSelectPanelData {
  selectedId?: string;
}

export interface TimecardAccountSelection {
  account: AccountModel;
}

const LOAD_LIMIT = 20;
const SCROLL_THRESHOLD_PX = 40;

/**
 * Server-side search over the site's demand accounts. A site can have thousands, so the list is paged
 * and filtered by the API rather than in the browser.
 */
require('./timecard-account-select-panel.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-account-select-panel',
  templateUrl: './timecard-account-select-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardAccountSelectPanelComponent {

  // PanelService closes the panel on this output, so the name is part of its contract.
  readonly onClose = output<TimecardAccountSelection | null>();

  private readonly data = inject<TimecardAccountSelectPanelData>(PANEL_DATA);
  private readonly accountsHttpService = inject(AccountsHttpService);

  readonly selectedId = this.data?.selectedId;
  readonly queryString = signal('');
  readonly accounts = signal<AccountModel[]>([]);
  readonly isLoading = signal(true);
  readonly isInitialLoading = signal(true);
  readonly isEmpty = computed(() => !this.isLoading() && !this.accounts().length);

  private cursor: string = null;
  private readonly reload$ = new Subject<void>();
  private readonly fetchNext$ = new Subject<string>();

  constructor() {
    this.reload$.pipe(
      switchMap(() => this.accountsHttpService.getAll(this.getQueryParams()).pipe(
        catchError(() => {
          this.setLoaded();
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe((response) => {
      this.cursor = response.cursor;
      this.accounts.set(response.list || []);
      this.setLoaded();
    });

    this.fetchNext$.pipe(
      switchMap((cursor) => this.accountsHttpService.getAll({...this.getQueryParams(), cursor}).pipe(
        catchError(() => {
          this.setLoaded();
          return EMPTY;
        }),
        // A new search replaces the list, so a page still in flight must not be appended to it.
        takeUntil(this.reload$)
      )),
      takeUntilDestroyed()
    ).subscribe((response) => {
      this.cursor = response.cursor;
      this.accounts.update((accounts) => accounts.concat(response.list || []));
      this.setLoaded();
    });

    // The panel sits on top of a dialog: without this, Escape would close the dialog under it.
    inject(OverlayRef).keydownEvents().pipe(
      filter((event) => event.key === 'Escape'),
      takeUntilDestroyed()
    ).subscribe(() => this.onClose.emit(null));

    this.reload();
  }

  onQueryChange(query: string) {
    this.queryString.set(query || '');
    this.reload();
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - SCROLL_THRESHOLD_PX;
    if (isNearBottom && this.cursor && !this.isLoading()) {
      this.isLoading.set(true);
      this.fetchNext$.next(this.cursor);
    }
  }

  onSelectId(accountId: unknown) {
    const account = this.accounts().find((item) => item.id === accountId);
    if (account) {
      this.onClose.emit({account});
    }
  }

  private reload() {
    this.cursor = null;
    this.isLoading.set(true);
    this.isInitialLoading.set(true);
    this.reload$.next();
  }

  private setLoaded() {
    this.isLoading.set(false);
    this.isInitialLoading.set(false);
  }

  private getQueryParams(): Record<string, string | number | boolean> {
    return {
      limit: LOAD_LIMIT,
      query: this.queryString(),
      isDemand: true,
      sort: 'name,asc'
    };
  }
}
